import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, getToken } from '../context/AuthContext';
import { useFlags } from 'launchdarkly-react-client-sdk';
import config from '../config';

export default function AccountPage() {
  const { user, logout } = useAuth();
  const { premiumFeaturesEnabled } = useFlags();
  const navigate = useNavigate();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [digestEnabled, setDigestEnabled] = useState(true);
  const [optionsDigestEnabled, setOptionsDigestEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState('');

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    fetch(`${config.apiBaseURL}/api/account`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(r => r.json())
      .then(data => {
        setAccount(data);
        setDigestEnabled(!!data.watchlist_digest_enabled);
        setOptionsDigestEnabled(!!data.options_digest_enabled);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load account information.');
        setLoading(false);
      });
  }, [user, navigate]);

  const handleToggle = async (field, currentValue, setFn, onMsg, offMsg) => {
    const next = !currentValue;
    setSaving(true);
    setSaveMsg('');
    try {
      const res = await fetch(`${config.apiBaseURL}/api/account`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [field]: next }),
      });
      if (!res.ok) throw new Error();
      setFn(next);
      setSaveMsg(next ? onMsg : offMsg);
    } catch {
      setSaveMsg('Failed to save preference. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleManageBilling = async () => {
    setPortalError('');
    setPortalLoading(true);
    try {
      const res = await fetch(`${config.apiBaseURL}/api/stripe/create-portal-session`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = await res.json();
      if (!res.ok) {
        setPortalError(data.error || 'Could not open billing portal');
        return;
      }
      window.location.href = data.url;
    } catch {
      setPortalError('Network error. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const subscriptionLabel = (status) => {
    if (premiumFeaturesEnabled) return 'Legacy Premium';
    const map = { active: 'Active', past_due: 'Past Due', canceled: 'Canceled', none: 'Free' };
    return map[status] || status;
  };

  const subscriptionColor = (status) => {
    if (premiumFeaturesEnabled || status === 'active') return '#007a3d';
    if (status === 'past_due') return '#b8860b';
    if (status === 'canceled') return '#cc2200';
    return '#888';
  };

  if (loading) return <div className="auth-page"><div className="auth-card"><p>Loading…</p></div></div>;

  return (
    <div className="auth-page" style={{ alignItems: 'flex-start', paddingTop: 48 }}>
      <div className="auth-card" style={{ maxWidth: 480 }}>
        <h2 style={{ marginBottom: 24 }}>My Account</h2>

        {error && <div className="auth-error">{error}</div>}

        {account && (
          <>
            <div className="account-field">
              <span className="account-label">Email</span>
              <span className="account-value">{account.email}</span>
            </div>

            <div className="account-field">
              <span className="account-label">Member Since</span>
              <span className="account-value">
                {new Date(account.created_at).toLocaleDateString('en-US', {
                  year: 'numeric', month: 'long', day: 'numeric',
                })}
              </span>
            </div>

            <div className="account-field">
              <span className="account-label">Subscription</span>
              <span
                className="account-value"
                style={{ color: subscriptionColor(account.subscription_status), fontWeight: 600 }}
              >
                {subscriptionLabel(account.subscription_status)}
              </span>
            </div>

            {account.subscription_status !== 'none' && (
              <div style={{ marginTop: 12 }}>
                <button
                  className="btn-secondary"
                  style={{ width: 'auto', padding: '8px 20px' }}
                  onClick={handleManageBilling}
                  disabled={portalLoading}
                >
                  {portalLoading ? 'Opening…' : 'Manage Billing'}
                </button>
                {portalError && <p className="auth-error" style={{ marginTop: 8 }}>{portalError}</p>}
              </div>
            )}

            <hr style={{ margin: '24px 0', borderColor: 'var(--border-color)' }} />

            <h3 style={{ margin: '0 0 16px', fontSize: 16, color: 'var(--primary-color)' }}>
              Email Preferences
            </h3>

            <div className="account-toggle-row">
              <div>
                <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>Daily Watchlist Digest</p>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
                  A morning email with your watchlist's top gainers, losers, and portfolio value score.
                </p>
              </div>
              <button
                className={`account-toggle ${digestEnabled ? 'account-toggle--on' : ''}`}
                onClick={() => handleToggle(
                  'watchlist_digest_enabled', digestEnabled, setDigestEnabled,
                  'Daily watchlist digest enabled.', 'You have unsubscribed from the watchlist digest.'
                )}
                disabled={saving}
                aria-label={digestEnabled ? 'Disable watchlist digest' : 'Enable watchlist digest'}
              >
                <span className="account-toggle-knob" />
              </button>
            </div>

            <div className="account-toggle-row" style={{ marginTop: 16 }}>
              <div>
                <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>Daily Options Screener Digest</p>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
                  A morning email with today's top 3 candidates from each options screener — CSP, Covered Calls, Put Debit Spreads, Wheel, and Long Straddle. Requires an active subscription.
                </p>
              </div>
              <button
                className={`account-toggle ${optionsDigestEnabled ? 'account-toggle--on' : ''}`}
                onClick={() => handleToggle(
                  'options_digest_enabled', optionsDigestEnabled, setOptionsDigestEnabled,
                  'Options screener digest enabled.', 'You have unsubscribed from the options screener digest.'
                )}
                disabled={saving}
                aria-label={optionsDigestEnabled ? 'Disable options digest' : 'Enable options digest'}
              >
                <span className="account-toggle-knob" />
              </button>
            </div>

            {saveMsg && (
              <p style={{
                marginTop: 12,
                fontSize: 13,
                color: saveMsg.includes('Failed') ? '#cc2200' : '#007a3d',
              }}>
                {saveMsg}
              </p>
            )}

            <hr style={{ margin: '24px 0', borderColor: 'var(--border-color)' }} />

            <button
              className="btn-secondary"
              style={{ width: 'auto', padding: '8px 20px' }}
              onClick={() => { logout(); navigate('/'); }}
            >
              Log Out
            </button>
          </>
        )}
      </div>
    </div>
  );
}
